import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "service/api";
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Badge,
  Spinner,
  Tooltip,
} from "reactstrap";
import ModalConfirm from "../Modal/ModalConfirm";
import ZendeskConnectorModal from "./ZendeskConnectorModal";
import WebhookSigningKeyModal from "./WebhookSigningKeyModal";
import {
  CONNECTOR_TYPES,
  CONNECTOR_TYPE_LABELS,
} from "../../enum/ConnectorTypes";
import newApi from "service/newApi";

const ConnectorsList = ({ clientId, clientName }) => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingConnector, setEditingConnector] = useState(null);
  const [zendeskModalOpen, setZendeskModalOpen] = useState(false);
  const [webhookKeyModalOpen, setWebhookKeyModalOpen] = useState(false);
  const [selectedConnectorForWebhook, setSelectedConnectorForWebhook] =
    useState(null);
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState({});

  useEffect(() => {
    if (clientId) {
      fetchConnectors();
    }
  }, [clientId]);

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      const response = await newApi.get(`/zendesk-connectors/${clientId}`);
      setConnectors(response.data || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error("Erro ao carregar conectores!");
      }
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnector = async (connectorId) => {
    try {
      await newApi.delete(`/zendesk-connectors/${connectorId}`);
      toast.success("Conector removido com sucesso!");
      fetchConnectors();
    } catch (error) {
      toast.error("Erro ao remover conector!");
    }
  };

  const handleToggleStatus = async (connectorId) => {
    setTogglingStatus(connectorId);
    try {
      const response = await newApi.patch(
        `/zendesk-connectors/${connectorId}/toggle-status`,
      );
      toast.success(response.data.message || "Status alterado com sucesso!");
      fetchConnectors();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao alterar status do conector!",
      );
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleEditConnector = (connector) => {
    setEditingConnector(connector);
    if (connector.connectorType === CONNECTOR_TYPES.ZENDESK) {
      setZendeskModalOpen(true); // ajustar
    }

    setZendeskModalOpen(true);
  };

  const handleNewConnector = (type) => {
    setEditingConnector(null);
    if (type === CONNECTOR_TYPES.ZENDESK) {
      setZendeskModalOpen(true);
    }
  };

  const handleConnectorSaved = () => {
    setEditingConnector(null);
    setZendeskModalOpen(false);
    setWebhookKeyModalOpen(false);
    setSelectedConnectorForWebhook(null);
    fetchConnectors();
  };

  const handleWebhookKeyAction = (connector) => {
    setSelectedConnectorForWebhook(connector);
    setWebhookKeyModalOpen(true);
  };

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getConnectorTypeLabel = (type) => {
    return CONNECTOR_TYPE_LABELS[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" />
          <p>Carregando conectores...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Conectores</h3>
          <div>
            <Button
              color="primary"
              size="sm"
              onClick={() => handleNewConnector(CONNECTOR_TYPES.ZENDESK)}
              className="me-2"
            >
              <i className="fas fa-plus me-1" /> Novo Conector Zendesk
            </Button>
            {/* Adicionar outros tipos de conectores aqui */}
          </div>
        </CardHeader>
        <CardBody>
          {connectors.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-plug fa-3x text-muted mb-3" />
              <p className="text-muted">
                Nenhum conector configurado para este cliente.
              </p>
              <Button
                color="primary"
                onClick={() => handleNewConnector(CONNECTOR_TYPES.ZENDESK)}
              >
                Configurar primeiro conector
              </Button>
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Webhook Key</th>
                  <th>Webhook URL</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {connectors.map((connector) => (
                  <tr key={connector.id}>
                    <td>
                      <Badge color="info" pill>
                        {getConnectorTypeLabel(connector.connectorType) ||
                          "Zendesk"}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={connector.is_active ? "success" : "warning"}
                        pill
                      >
                        {connector.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Badge
                          color={
                            connector.config?.has_webhook_key
                              ? "success"
                              : "warning"
                          }
                          pill
                          className="me-2"
                        >
                          {connector.config?.has_webhook_key ? (
                            <>
                              <i className="fas fa-check-circle me-1" />
                              Configurada
                            </>
                          ) : (
                            <>
                              <i className="fas fa-exclamation-circle me-1" />
                              Pendente
                            </>
                          )}
                        </Badge>
                        {!connector.config?.has_webhook_key && (
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => handleWebhookKeyAction(connector)}
                          >
                            <i className="fas fa-key me-1" />
                            Configurar
                          </Button>
                        )}
                        {connector.config?.has_webhook_key && (
                          <Button
                            color="outline-secondary"
                            size="sm"
                            onClick={() => handleWebhookKeyAction(connector)}
                          >
                            <i className="fas fa-edit me-1" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <code
                          className="small text-truncate me-2"
                          style={{ maxWidth: "200px" }}
                        >
                          {connector.webhookUrl}
                        </code>
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => {
                            const webhookUrl = `${process.env.REACT_APP_ZENDESK_CONNECTOR_WEBHOOK_URL}?apikey=${process.env.REACT_APP_ZENDESK_API_KEY}&clientId=${clientId}`;
                            navigator.clipboard.writeText(webhookUrl);
                            toast.success("URL copiada!");
                          }}
                        >
                          <i className="fas fa-copy" />
                        </Button>
                      </div>
                    </td>
                    <td>{formatDate(connector.created_at)}</td>
                    <td>
                      <div className="btn-group">
                        <Button
                          color="primary"
                          size="sm"
                          id={`edit-btn-${connector.id}`}
                          onClick={() => handleEditConnector(connector)}
                        >
                          <i className="fas fa-edit" />
                        </Button>
                        <Tooltip
                          placement="top"
                          isOpen={tooltipOpen[`edit-${connector.id}`]}
                          target={`edit-btn-${connector.id}`}
                          toggle={() => toggleTooltip(`edit-${connector.id}`)}
                        >
                          Editar conector
                        </Tooltip>

                        <Button
                          color={connector.is_active ? "warning" : "success"}
                          size="sm"
                          id={`toggle-btn-${connector.id}`}
                          disabled={togglingStatus === connector.id}
                          onClick={() => handleToggleStatus(connector.id)}
                        >
                          {togglingStatus === connector.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i
                              className={`fas fa-${
                                connector.is_active ? "pause" : "play"
                              }`}
                            />
                          )}
                        </Button>
                        <Tooltip
                          placement="top"
                          isOpen={tooltipOpen[`toggle-${connector.id}`]}
                          target={`toggle-btn-${connector.id}`}
                          toggle={() => toggleTooltip(`toggle-${connector.id}`)}
                        >
                          {connector.is_active ? "Desativar" : "Ativar"}{" "}
                          conector
                        </Tooltip>

                        <ModalConfirm
                          id={connector.id}
                          modalText={`Deseja remover este conector ${getConnectorTypeLabel(
                            connector.connectorType,
                          )}?`}
                          handler={() => handleDeleteConnector(connector.id)}
                          icon="fas fa-trash"
                          buttonColor="danger"
                          buttonSize="sm"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Modal do Zendesk */}
      <ZendeskConnectorModal
        isOpen={zendeskModalOpen}
        toggle={() => setZendeskModalOpen(false)}
        clientId={clientId}
        connector={editingConnector}
        onSave={handleConnectorSaved}
      />

      {/* Modal da Webhook Signing Key */}
      <WebhookSigningKeyModal
        isOpen={webhookKeyModalOpen}
        toggle={() => setWebhookKeyModalOpen(false)}
        connector={selectedConnectorForWebhook}
        onSave={handleConnectorSaved}
      />
    </>
  );
};

export default ConnectorsList;
